export type Message = {
  message?: string;
  data?: any;
};

export interface MessageEvent {
  data?: any;
  source?: any;
}

export default class PostMessageService {
  private receiverWindow: Window;

  private messageSubscriber: {
    [key: string]: (data: any, sourceFrame: Window | HTMLIFrameElement) => void;
  } = {};

  public constructor(receiverWindow?: Window) {
    if (receiverWindow) {
      this.receiverWindow = receiverWindow;
    }
    this.handlePostMessage = this.handlePostMessage.bind(this);
    this.initPostMessageListener();
  }

  public setReceiver(receiverWindow: Window): void {
    this.receiverWindow = receiverWindow;
  }

  public listener<T>(
    message: string,
    handler: (data: T, sourceFrame: Window | HTMLIFrameElement) => any
  ): void {
    this.messageSubscriber[message] = handler;
  }

  public sendAmpResize(height: number): void {
    this.receiverWindow.postMessage(
      {
        sentinel: "amp",
        type: "embed-size",
        height: height,
      },
      "*"
    );
  }

  public sendAppResize(height: number): void {
    // Event handle "needsRefresh" is defined here
    // https://github.com/spring-media/apps-ios-news/blob/e9eeddfd341d865090d9c99bdd77bc7d7ad21bf9/Widgetizr%20Sandbox/LeanWidgets
    // /WebView/WebViewJanusWidgetCell.swift#L264
    (window as any).webkit?.messageHandlers?.needsRefresh?.postMessage({
      documentHeight: height,
      documentOffsetHeight: height,
    });
  }

  public send<T>(
    message: string | Message,
    answerCallback?: (data: T, sourceFrame: Window | HTMLIFrameElement) => void
  ): any {
    if (!this.receiverWindow) {
      throw new Error("Missing receiver window");
    }

    if (answerCallback) {
      const messageName =
        typeof message === "string" ? message : message.message;
      this.messageSubscriber[`${messageName}_response`] = answerCallback;
    }

    this.receiverWindow.postMessage(message, "*");
  }

  private initPostMessageListener(): void {
    window.addEventListener("message", this.handlePostMessage);
  }

  private handlePostMessage(event: MessageEvent): any {
    const { message, data } = event.data;
    if (!message || !this.messageSubscriber[message]) {
      return;
    }

    const iFrame = this.findRealIframe(event.source);
    // IFrame not found: The direction of the communication is top window to iFrame
    // Use the Window object event.source as sourceFrame
    const sourceFrame = iFrame ? iFrame : event.source;
    const response = this.messageSubscriber[message](data, sourceFrame);

    if (response as any) {
      event.source.postMessage(
        {
          message: `${message}_response`,
          data: response,
        },
        "*"
      );
    }
  }

  private findRealIframe(sourceWindow: Window): HTMLIFrameElement | undefined {
    const iframes = document.getElementsByTagName("iframe");
    for (let i = 0; i < iframes.length; i++) {
      if (iframes[i].contentWindow === sourceWindow) {
        return iframes[i];
      }
    }
  }
}
